/**
 * One brand-supplied business figure, as a titled block (VRG-459).
 *
 * ONE component for BOTH surfaces — `Settings -> Business figures` and the in-card
 * popover — rather than two that happen to look alike. The title, the inline unit,
 * the parsed echo, the consequence and the provenance line are the parts a reader
 * checks a number against, and a second copy of them is how one surface ends up
 * with the magnitude echo and the other without it (§36).
 *
 * ## It reads title -> description -> relationship -> input
 *
 * The figure's name is a CARD TITLE at the house 20px, not a 14px field label, and
 * the explanation sits under it rather than under the control. That ordering is
 * §40's (descriptive copy directly under the title it describes), and it is what
 * makes a figure a self-contained unit — which matters because the page is
 * SEARCHABLE: a filtered result has to stand on its own rather than be a surviving
 * row of a card whose siblings vanished.
 *
 * The header is `CardSectionHeader`, the house title+description block, NOT a
 * hand-rolled Typography pair (§28). Its title treatment is deliberately identical
 * to `@repo/ui` `Card`'s own `title`, so a figure block and any other card on the
 * platform carry the same title.
 *
 * ## Field chrome still comes from ONE place (§31)
 *
 * The border, hover, focus ring and error state are `@repo/ui` `Input`'s. Nothing
 * here hand-rolls a border.
 *
 * ⚠️ The `Input` carries NO `label`, deliberately — the card title IS the field's
 * name, and rendering both would print the same words twice, eight px apart. A
 * control with no visible label still needs an accessible one, so the title is
 * attached via `aria-label`. This is not a §31 violation: §31 governs where a field
 * LABEL's typography and gap come from, and this block has no field label to style —
 * it has a card title, which `CardSectionHeader` owns.
 *
 * ## The permission split (§27)
 *
 * `analytics_input_edit` gates EDITING. Reading is `view_analytics`, server-side and
 * here — so without the write permission the field still renders, still shows the
 * value and the provenance, and every ACTION is disabled with a tooltip naming the
 * permission. Hiding it would make the figure it drives unauditable to exactly the
 * person who can see the figure.
 *
 * The INPUT itself goes `readOnly` rather than `disabled` — see the prop for why a
 * 0.4-opacity fade defeats the reason this page is view-gated in the first place.
 */
import { useEffect, useMemo, useState } from 'react'

import { alpha, Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { CircleAlertIcon, CircleCheckIcon, CircleDashedIcon, InfoIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { Button, Input } from '@repo/ui'
import { METRIC_BY_ID } from '@repo/types'
import type {
  IMetricInputDefinition,
  IMetricInputValue,
  IRoiSummary,
} from '@repo/types'

import { AnalyticsCategoryGlyph } from 'components/display/AnalyticsCategoryLabel'
import { CardSectionHeader } from 'components/display/CardSectionHeader'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useCanAccess } from 'hooks'

import {
  describeConsequence,
  describeSettingLine,
} from './metricInputConsequence'
import {
  describeProvenance,
  groupEntryForDisplay,
  metricInputCurrency,
  metricInputMarkerState,
  metricInputStale,
  parseMetricInput,
  storedToEntry,
  unitAdornment,
  type MetricInputMarkerState,
} from './metricInputFormat'

export const ANALYTICS_INPUT_EDIT = 'analytics_input_edit' as const

/** §27 — the tooltip has to name the permission, not say "ask an admin". */
export const NO_EDIT_PERMISSION_TOOLTIP =
  'You need the “Edit analytics inputs” permission to change this. Ask an admin to grant it.'

/**
 * The reading measure for a FIGURE'S DESCRIPTION, in px. MEASURED, not chosen by eye.
 *
 * The page's body copy is 14px Lato, whose mean glyph advance measured 5.98px in the
 * browser. So a container's width maps directly onto characters per line:
 *
 *     1100px (the settings NARROW_COLUMN_WIDTH)   184 CPL
 *      720px (this page's first attempt)          120 CPL
 *      560px (here)                                94 CPL
 *      449px (75 CPL, the classic upper bound)     75 CPL
 *
 * A description measures ~100 characters and renders as two lines at 560 — about 50
 * CPL, inside the 45–90 range for UI body copy. That is the case the range is FOR:
 * these repeat down a long list, one per card, and are read as prose with a return
 * sweep on every line.
 *
 * ## ⚠️ THE INTRO PARAGRAPH NO LONGER USES THIS, and it was a mistake that it ever did
 *
 * The constant was shared with the page's intro on the reasoning that one surface
 * should have one prose width. That produced two successive NARROWINGS (720, then 560)
 * of a paragraph the reader kept asking to see WIDER, which is the signal that the
 * measurement was answering the wrong question — and it was:
 *
 * The 45–90 CPL range governs SUSTAINED reading, where the eye tracks back to the start
 * of the next line dozens of times and a long measure makes it lose the row. The intro
 * is a STANDFIRST — read once, on arrival, and at the full column it is two lines. With
 * two lines there is one return sweep, so the cost the range exists to prevent is
 * essentially not paid. A rule for paragraphs was being applied to a caption.
 *
 * So the two are no longer one number: the intro fills the column (see
 * `BusinessFiguresPage`), the descriptions keep this. Different kinds of text, measured
 * against different questions.
 */
export const PROSE_MEASURE_PX = 560

/**
 * "Used by <metric>" — the figure's RELATIONSHIP, rendered as a provenance row above the input.
 *
 * ⚠️ NOT the input's label, though this docstring said so until 2026-09-10 and the render site
 * wrapped it in `FieldLabel` accordingly. The control's accessible name is its `aria-label`
 * and its visible name is the card title; this states what the figure FEEDS. The mislabelling
 * had a visible cost — it inherited a field label's 8px gap and crowded the input.
 *
 * ## ⚠️ IT WAS A BORDERED BLOCK, AND THE FORMULA IS WHY. Both are gone
 *
 * This used to be a bordered, tinted panel carrying the analytics glyph, a "Used by ..."
 * sentence AND each metric's registry `formula` spelled out beneath it. The box existed for
 * one job: the formula made this three-or-more lines of prose, and without a border that
 * read as a third paragraph of sub-field text rather than as a different KIND of statement.
 *
 * The formula has moved to a tooltip on the metric's name (see below), so the block is one
 * short line — "Used by Return Fraud Prevented" — and the box has nothing left to do. A
 * border around a single line is chrome around nothing, so it is now a plain label sitting
 * above the control, which is what a one-line statement of "what this field feeds" is.
 *
 * ## ⚠️ THE FORMULA MOVED BECAUSE IT WAS PRINTED TWICE ON ONE SCREEN
 *
 * `Counterfeit rate` and `Gross margin` BOTH feed Counterfeit Deflection, so both cards
 * rendered the identical three-line formula — visible in the four-card grid at once. That is
 * §44's define-once argument applied to on-screen repetition rather than to source: the
 * duplication is not a maintenance risk here (both already read the single registry entry),
 * it is a READING cost, because the same paragraph twice on one screen is text the eye has
 * to process and discard.
 *
 * A hover is the right home for it: the formula answers "how exactly is my number used",
 * which is a question asked once per metric, not a thing to re-read on every card that
 * happens to feed it.
 *
 * ⚠️ `text`, NOT `term`. §44 wants a glossary entry read by the tooltip wherever one exists,
 * and `docs/glossary.json` has NO entry for any metric name (checked: 89 entries, none for
 * "Return Fraud Prevented", "Counterfeit Deflection" or "Customer Acquisition"). So the body
 * is the registry's own `formula` — still a single source, just a different one — and there
 * is no footer, because a footer would link to a definition that does not exist. If metric
 * entries are ever added to the glossary, switch this to `term` and delete the `text`.
 *
 * ## Why this row owns its own gap, and §31 is satisfied rather than bent
 *
 * §31 fixes the label→control gap at 8px inside `FieldLabel` so no call site invents its
 * own. This row is not a field label, so it is not that gap: it sets 16px on its wrapper
 * (see the render site) and `FieldLabel` keeps owning the number §31 exists to protect.
 *
 * ⚠️ This block previously said the opposite — that the row went through `FieldLabel` to
 * inherit the canonical gap. That was the framing that produced the defect: the row brings
 * its own `Stack`, gauge glyph and `body3`/`text.secondary` `Typography`, so `FieldLabel`
 * contributed nothing except a field label's margin, which read as crowding under a line
 * carrying an icon and an underlined link.
 */
/**
 * The metrics a figure feeds, as a sentence — "Return Fraud Prevented", or "A, B and C".
 *
 * Pluralised and joined from the ARRAY, never from two hand-written strings per figure,
 * so a figure that starts feeding a second metric reads correctly with no edit here. The
 * final "and" is what stops three metrics reading as a CSV.
 */
const joinMetricTitles = (titles: string[]): string =>
  titles.length <= 1
    ? (titles[0] ?? '')
    : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`

/** What a reader loses by leaving this figure blank. See `StandingLine`. */
const describeUnsetConsequence = (definition: IMetricInputDefinition): string => {
  const titles = definition.metric_titles
  if (titles.length === 0) return 'Not supplied yet.'
  return titles.length === 1
    ? `Without this, ${titles[0]} shows a dash in Analytics.`
    : `Without this, ${joinMetricTitles(titles)} show a dash in Analytics.`
}

/**
 * One metric's name, carrying its formula on hover.
 *
 * The trigger is the NAME ITSELF rather than a help glyph beside it — the words are what a
 * reader would point at to ask "what does this one do", and a 14px dot next to a 14px name
 * is a smaller target than the name (`InfoTooltip`'s own docstring makes the same argument
 * for stat labels). The dotted underline is the only rest-state cue that there is more here,
 * and it is the standard "hover for a definition" mark rather than a link's solid rule.
 */
const MetricNameWithFormula = ({ id, title }: { id: string; title: string }) => {
  const formula = METRIC_BY_ID[id]?.formula
  if (!formula) return <>{title}</>

  return (
    <InfoTooltip text={formula} placement="top">
      <Box
        component="span"
        sx={{
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '3px',
          cursor: 'help',
        }}
      >
        {title}
      </Box>
    </InfoTooltip>
  )
}

/**
 * "Used by A, B and C", with each name individually hoverable.
 *
 * ⚠️ The joining is done HERE rather than through `joinMetricTitles`, because that helper
 * builds a STRING and each name now has to be its own element to carry its own tooltip.
 * The two must produce the same prose — ", " between, " and " before the last — so if the
 * conjunction ever changes, change both. The helper is still the single source for the
 * standing line, which genuinely wants a string.
 */
const UsedByLabel = ({ definition }: { definition: IMetricInputDefinition }) => {
  const titles = definition.metric_titles
  if (titles.length === 0) return null

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ display: 'flex' }}>
        {/*
          ⚠️ From `AnalyticsCategoryGlyph`, NOT a local `GaugeIcon` import. That component's
          docstring is explicit that a second `category -> glyph` map "is how a category added
          later ends up with a gauge in one place and nothing in the other", and importing the
          icon directly recreates precisely that duplicate map, spelled as a literal instead of
          a table. The gauge is what the card library prints beside every metric-form card, so
          a reader meets the same mark for the same concept in both places.
        */}
        <AnalyticsCategoryGlyph category="metric" size={16} strokeWidth={1} />
      </Box>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        Used by{' '}
        {definition.metric_ids.map((id, i) => (
          <Box component="span" key={id}>
            {i > 0 && (i === definition.metric_ids.length - 1 ? ' and ' : ', ')}
            <MetricNameWithFormula id={id} title={titles[i] ?? id} />
          </Box>
        ))}
      </Typography>
    </Stack>
  )
}

/**
 * "Which of these still need me?" — answered beside the TITLE, so a grid of figures is
 * scannable without reading a single one of them.
 *
 * ## ⚠️ THE MARKED STATE IS THE ONE THAT NEEDS ACTION. That inversion IS the fix
 *
 * The first version marked only the DONE figures, with a check, and nothing at all on
 * the ones still outstanding. It was reported as "not noticeable enough", and enlarging
 * or brightening the check would not have touched the cause: a reader was being asked to
 * spot an ABSENCE, which only works if they already know a mark would otherwise be
 * there. Scaling the check up makes the finished cards louder while the ones actually
 * wanting work stay invisible — precisely backwards.
 *
 * So the loud marker is now on UNSET. Same reason error states are loud and success
 * states are quiet: the eye should be pulled to what is outstanding.
 *
 *   unset         "Needed"        FILLED amber chip     — the scan target
 *   unconfirmed   "Needs review"  FILLED amber chip     — supplied, but wants a look
 *   confirmed     "Supplied"      FILLED purple chip    — quiet, nothing to do
 *
 * ## ⚠️ ALL THREE ARE THE SAME OBJECT. The bare glyph was the mistake
 *
 * `confirmed` rendered as a 16x16 tick with no chip around it, sitting in the same slot
 * as a 75px labelled pill. That is two vocabularies for ONE axis: a reader scanning the
 * column has to learn that "a small tick" and "a labelled pill" answer the same question,
 * and the tick — being the only unlabelled one — is also the only state whose meaning
 * cannot be read without hovering. All three are now pills of the same family, so the
 * axis has one shape and every state carries its own word.
 *
 * Making `confirmed` a pill does NOT make it loud, which was the fear: it is brand purple
 * rather than amber, so it stays out of the attention family entirely. The inversion this
 * component was built around — the marked state is the one that needs action — survives on
 * HUE rather than on fill.
 *
 * ## "Needs review", not "Unconfirmed" and not "Needs update"
 *
 * The `Needs …` family is right: it matches "Needed" and it tells the reader what to DO
 * rather than naming a state they then have to translate into an action.
 *
 * The VERB is where the proposed "Needs update" fails. A provisional figure came from the
 * signup form and is very often correct — the thing outstanding is that nobody has
 * CONFIRMED it, not that it is wrong. "Update" asserts the number needs changing, which
 * invites someone to edit a correct value; it also contradicts the affordance next to it,
 * which says "Confirm this figure". "Review" names the real action (look at it, then
 * confirm or correct) and asserts nothing about the number.
 *

 * ## ⚠️ THE TWO AMBER STATES NO LONGER SEPARATE ON FILL. That is a KNOWN, ACCEPTED trade
 *
 * "Needed" and "Unconfirmed" are both attention states, so both are amber. They used to be
 * separated by FILL — solid amber against transparent — which was chosen precisely because
 * it is a STRUCTURAL difference rather than a second shade, and therefore survives greyscale
 * and every form of CVD intact where a hue pair does not.
 *
 * All three pills are now filled (2026-08-29, user directive: "Supplied and Needs Review
 * should be filled bg like 'Needed' — looks better like this without border, cleaner"). So
 * the two amber chips are now IDENTICAL in ground and ink, and separate on exactly two
 * channels:
 *
 *   GLYPH    a dashed open ring (nothing in the slot yet) against an exclamation (there
 *            is something here, and it wants checking).
 *   WORD     "Needed" against "Needs review".
 *
 * ⚠️ Do not "restore" the outline as a robustness fix without re-reading this. The trade was
 * made deliberately, on WHICH distinction each channel carries:
 *
 *   done vs not-done      must survive a glance across a whole grid, and is carried by HUE
 *                         (brand purple vs amber). UNTOUCHED by this change — the fill is a
 *                         different alpha of a different colour, so the resolved state still
 *                         reads as a different object at any distance.
 *   Needed vs Needs review is a finer distinction between two states that BOTH mean "this
 *                         wants your attention". A reader who has to tell them apart is
 *                         already looking at that one card, at which point the glyph and the
 *                         word are enough.
 *
 * Because fill no longer carries any of it, the GLYPHS were strengthened to take the load:
 * 14px at `strokeWidth` 2, up from 12px at 1.5. The pill's footprint is unchanged by that —
 * the 2px the glyph gains is exactly the 2px the dropped border gives back, measured 22px
 * tall either way — so the pill still sits as a peer of the 20px title rather than growing
 * into a superscript.
 *
 * A blue/amber pair was the other candidate and is the most CVD-robust HUE contrast
 * available. It was rejected because it would demote "unconfirmed" out of the warning
 * family, and this card already states that standing in `warning.main` on its provenance
 * line — the chip would then disagree with the sentence eight lines below it.
 *
 * ## The confirmed pill stays brand purple, and it is correctly the quiet state
 *
 * It shipped `success.main` for one revision, on the argument that this surface puts a
 * figure's state on the ALERT axis so green reads as the resolution of an amber. That
 * was rejected on sight, and the `SetupChecklist` precedent it argued against is the one
 * that holds: "a completed setup step is not a 'success' state in the alert sense —
 * nothing went right or wrong — it is progress through the product, so it reads in the
 * product's own colour". `brand[500]` is the same value that checklist uses, so the
 * product's two completion marks are one colour. (Measured, since a mark carrying
 * meaning owes 3:1 — `#8F68EA` on the dark card fill `rgb(15,15,18)` is 4.86:1.)
 *
 * ## No card-level tint or border, deliberately
 *
 * §34 offers a tinted card ground for this kind of state, and it is the wrong instrument
 * here: on a fresh account EVERY figure is unset, so a tint would wash the entire grid
 * amber and the marker would discriminate nothing at exactly the moment it matters most.
 * A brand BORDER is unavailable for a different reason — §34 reserves it for "selected",
 * and borrowing it would mean two things on one page. A chip is a fixed-size
 * signal that reads the same whether one card or eight carry it.
 *
 * Each chip carries its state in TEXT, so nothing here depends on a reader seeing colour
 * at all; the glyphs are `aria-hidden` beside their own labels.
 */
/**
 * The three pills, as data rather than as three branches — so the label a card shows, the
 * word the page header counts in and the option in the status filter are one vocabulary.
 * `MARKER_LABELS` is exported for exactly that: `BusinessFiguresPage` reads it instead of
 * retyping "Needed" / "Needs review" into its own filter options, which is how the chip
 * and the filter came to be capable of disagreeing.
 */
export const MARKER_LABELS: Record<MetricInputMarkerState, string> = {
  unset: 'Needed',
  unconfirmed: 'Needs review',
  confirmed: 'Supplied',
}

/**
 * The glyph per state, exported for the SAME reason `MARKER_LABELS` is.
 *
 * The status filter's dropdown renders each option as this glyph plus its label, so the
 * option a reader picks carries the identical mark to the pill they picked it by. A second
 * `state -> icon` map on the page would be the drift `MARKER_LABELS`' own comment describes,
 * one field over: the day a glyph changes here, the menu would keep the old one and the two
 * surfaces would disagree about what "Needs review" looks like.
 */
export const MARKER_GLYPHS: Record<MetricInputMarkerState, LucideIcon> = {
  unset: CircleDashedIcon,
  unconfirmed: CircleAlertIcon,
  confirmed: CircleCheckIcon,
}

const MARKER_TOOLTIPS: Record<MetricInputMarkerState, string> = {
  unset: 'Not supplied yet. The metrics below show a dash until you enter it.',
  unconfirmed:
    'Supplied, but nobody has confirmed it. It came from your signup form and is already driving the metrics below. Confirm it, or correct it.',
  confirmed: 'Supplied. This figure is confirmed and is being used by the metrics below.',
}

const MetricInputMarker = ({ state }: { state: MetricInputMarkerState }) => {
  const theme = useTheme()

  // Brand for the resolved state, warning for the two that are outstanding. Purple-vs-amber
  // is now the ONLY colour channel on this axis — the fill tier that used to separate the
  // two amber pills is gone (see the header block), so those two lean entirely on glyph and
  // word. Hue still carries done-vs-not-done, which is the distinction that has to survive a
  // glance across the grid.
  const tone = state === 'confirmed' ? theme.palette.brand[500] : theme.palette.warning.main
  // From the shared map, not a local ternary — the status dropdown renders the same glyphs.
  const Glyph = MARKER_GLYPHS[state]

  return (
    <Tooltip arrow placement="top" title={MARKER_TOOLTIPS[state]}>
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          display: 'inline-flex',
          cursor: 'help',
          color: tone,
          /*
            12px horizontal / 4px vertical, up from 8/2. MEASURED: the pill goes 75.4x18
            to 87.4x22 ("Needed"). At 8/2 the label sat 8px from a rounded edge that is
            itself 8px in radius, so the text ran into the corner curve on both sides and
            the chip read as a tint applied to a word rather than as an object. 12 clears
            the radius; 4 gives the 12px label a full half-line of air, which is what makes
            a 22px pill sit as a peer of the 20px title instead of as a superscript.
          */
          px: 3,
          py: '4px',
          borderRadius: 2,
          /*
            NO BORDER, and all three at the same 0.16 ground. The border went with the
            outline tier: with every pill filled, a 1px edge at the same hue as the fill
            reads as a seam on the chip rather than as a second tier, which is the "cleaner
            without border" the change was asked for.

            Dropping it costs 2px of height and 2px of width, which the glyph's growth from
            12 to 14 puts straight back — so the pill is the same 22px object it measured
            before and nothing in the title row moves.
          */
          backgroundColor: alpha(tone, 0.16),
        }}
      >
        {/*
          14/2, up from 12/1.5. With fill no longer separating "Needed" from "Needs review",
          the dashed ring and the exclamation are one of the only two channels left — so they
          are drawn heavily enough to tell apart at a glance instead of at a squint.
        */}
        <Glyph size={14} strokeWidth={2} aria-hidden />
        <Typography variant="body4" sx={{ fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap' }}>
          {MARKER_LABELS[state]}
        </Typography>
      </Stack>
    </Tooltip>
  )
}

/**
 * ── THE FIGURE'S STANDING: ONE LINE, ONE SLOT, ALWAYS PRESENT ────────────────────────
 *
 * Exactly one sentence, in exactly one place, saying where this figure stands:
 *
 *   unset         (!) "Without this, <metric> shows a dash in Analytics."
 *   unconfirmed   (!) "From signup, unconfirmed"
 *   confirmed         "Set in Vera on Aug 28, 2026"
 *
 * ## ⚠️ NONE OF THESE ARE AMBER ANY MORE. The pill already said it
 *
 * The first two rendered in `warning.main`. They are now `text.secondary` with a leading
 * alert glyph, and the reason is a budget argument rather than a taste one: the PILL beside
 * the title now carries this card's status, so an amber sentence restates in colour what the
 * pill has already said in colour eight lines above it. On a fresh account every figure is
 * unset, so the grid showed four amber pills AND four amber sentences — the entire warning
 * budget of the page spent on the NORMAL state of a surface whose whole job is to list what
 * is missing. Colour that is always on is not a signal.
 *
 * The glyph is what survives the demotion: it keeps the line marked as "something is
 * outstanding here" without a second colour, and it is the same `CircleAlertIcon` the
 * `unconfirmed` pill uses, so the mark is consistent with the state it describes.
 *
 * The `confirmed` line deliberately gets NO glyph — an alert mark on "Set in Vera on Aug 28"
 * would mark a settled fact as outstanding. That is the one state with nothing to do, so the
 * asymmetry is the meaning rather than an inconsistency.
 *
 * ## ⚠️ THIS EXISTS BECAUSE THE CARD USED TO JUMP, AND THE NUMBERS ARE WHY
 *
 * The three states were rendered in TWO different slots — the warning inside the "Used
 * by" panel near the top, the provenance line near the bottom — and each collapsed to
 * nothing in the states it did not apply to. Because they are mutually exclusive by
 * construction (unset => warning, set => provenance), the card's height changed by the
 * DIFFERENCE between two boxes in different places every time a figure's state moved.
 *
 * Measured on the live page at 1280x800 with the sidebar expanded (a 788.5px settings
 * column, 388px cards), per card content height:
 *
 *     Customer acquisition cost   confirmed 355  ->  cleared 371     +16
 *     Gross margin                provisional 395 -> cleared 371     -24
 *     Gross margin                provisional 395 -> confirmed ~355  -40   (the worst,
 *                                                                      and it fires on
 *                                                                      "Confirm", not
 *                                                                      on Clear)
 *
 * And because the cards are `height: 100%` inside a `Grid` row, a card that governs its
 * row drags its SIBLING with it: clearing Gross margin took BOTH cards in row two from
 * 445 to 421 and moved everything below up 24px. That is the "clicking remove shifts
 * everything oddly" report — the odd part is that the card you did not touch resized.
 *
 * The fix is footprint parity (§19) applied to a state machine rather than to a loading
 * state: put the mutually-exclusive sentences in ONE always-rendered box, and give that
 * box a floor of two lines so a one-line variant does not shift against a two-line one.
 * The height then does not depend on the state at all.
 *
 * `minHeight` is expressed as two lines of the 14px/1.5 body3 it renders (42px) rather
 * than as a magic number, so it follows the type if the type moves.
 */
const STANDING_LINE_HEIGHT = 1.5
const STANDING_MIN_LINES = 2
const STANDING_MIN_HEIGHT_PX = Math.round(14 * STANDING_LINE_HEIGHT * STANDING_MIN_LINES)

interface MetricInputFieldProps {
  definition: IMetricInputDefinition
  /** This company's stored value for this key, or null. */
  value: IMetricInputValue | null
  /**
   * The ROI response the reader is currently looking at, for the consequence echo.
   * Null is fine — the echo degrades to the parsed value and its magnitude.
   */
  roi: IRoiSummary | null
  /**
   * The company's `reporting_currency` — the currency a NEW amount is entered in
   * (VRG-497).
   *
   * ⚠️ NOT the currency an EXISTING amount is displayed in. A stored value is drawn in
   * its own `value.currency`; this is only the answer for an empty field, and the
   * yardstick the stored one is compared against to decide whether it is stale. The two
   * are separate on purpose — see `metricInputFormat`'s header.
   */
  reportingCurrency: string
  /**
   * Save this value (API units), IN THE CURRENCY THE FIELD WAS ASKING IN. Rejects are
   * surfaced by the caller.
   *
   * ⚠️ THE SECOND ARGUMENT IS NOT DECORATION. Editing a figure stored in USD while the
   * company reports in GBP keeps it in USD — the field showed `$` throughout, so saving
   * it as GBP would relabel the number the reader just typed against the symbol beside
   * it. That is the same silent restatement the read rule exists to prevent, arriving
   * through the write path instead. Moving a figure INTO the reporting currency is a
   * deliberate act (clear it, then enter it fresh), not a side effect of editing it.
   */
  onSave: (storedValue: number, currency: string) => Promise<void> | void
  /** Retract the stored value entirely. Absent = no remove affordance. */
  onRemove?: () => Promise<void> | void
  isSaving?: boolean
  /**
   * Show which metrics this field feeds. On by default — a number asked for with no
   * stated purpose is the thing "Used by ..." exists to prevent. The popover turns
   * it off, because there the metric IS the surrounding card.
   */
  showUsedBy?: boolean
  /**
   * Render as a FIELD rather than as a titled card block.
   *
   * The page gives each figure a 20px card title with its description beneath — the
   * self-contained unit a search result has to be. A 420px popover is not a card and
   * cannot carry that: its own heading is the METRIC, so a 20px figure name under a
   * 16px metric name would invert the hierarchy, and the description would take a
   * third of the panel.
   *
   * So compact mode goes back to the ordinary field arrangement — `Input`'s own
   * `FieldLabel` (§31's single source for label typography and the label->control
   * gap) with the description under it. Both modes use a house component; which one
   * is right depends on whether the thing being rendered is a card or a field.
   */
  compact?: boolean
}

export const MetricInputField = ({
  definition,
  value,
  roi,
  reportingCurrency,
  onSave,
  onRemove,
  isSaving = false,
  showUsedBy = true,
  compact = false,
}: MetricInputFieldProps) => {
  const theme = useTheme()
  const { isHasAccess: canEdit } = useCanAccess({ action: ANALYTICS_INPUT_EDIT })

  const stored = value?.value ?? null
  const savedEntry = stored === null ? '' : storedToEntry(definition, stored)

  /*
    ── THE CURRENCY THIS FIELD IS SPEAKING IN (VRG-497) ───────────────────────────────

    A stored amount keeps its OWN currency; an empty field takes the company's. One
    expression, used by the adornment, both echoes, the bound messages and the save — so
    the symbol in the box, the words under it and the row that gets written cannot
    disagree about what the number means.

    `stale` is the case that used to be invisible: the figure is real, the brand entered
    it, and it is denominated in something the company no longer reports in. It is KEPT
    (never converted, never force-re-entered) and it says so.
  */
  const fieldCurrency = metricInputCurrency(value, reportingCurrency)
  const stale = metricInputStale(value, reportingCurrency)

  const [draft, setDraft] = useState(savedEntry)

  /**
   * Re-seed from the server whenever the SAVED value changes.
   *
   * Keyed on `savedEntry` and not on the whole `value` object: the response is a new
   * object identity on every refetch, so keying on it would clobber whatever the
   * user is typing every time a sibling field saved. This resets only when the
   * number this field shows actually moved.
   */
  useEffect(() => {
    setDraft(savedEntry)
  }, [savedEntry])

  const parsed = useMemo(
    () => parseMetricInput(definition, draft, fieldCurrency),
    [definition, draft, fieldCurrency],
  )

  const isDirty = draft.trim() !== savedEntry
  const adornment = unitAdornment(definition, fieldCurrency)

  /**
   * Focus, tracked ONLY so the field can group its digits while nobody is typing.
   *
   * See `groupEntryForDisplay`. `2500000` is not readable as two and a half million,
   * and this page exists to be audited — so a resting value is shown as `2,500,000` and
   * reverts to the raw digits the moment the caret enters, which is what keeps grouping
   * out of the draft and therefore out of `isDirty` and off the caret.
   *
   * Gated on `canEdit` as well: a read-only field is still focusable, and ungrouping a
   * number for a reader who cannot change it would be the one case where the swap buys
   * nothing at all.
   */
  const [focused, setFocused] = useState(false)
  const showRawEntry = focused && canEdit

  /**
   * The consequence, computed only for a value that is actually about to be saved.
   *
   * Gated on `isDirty` as well as validity: recomputing it for the value already
   * stored would render "goes from $X to $X", which reads as a change and is not one.
   */
  const consequence = useMemo(() => {
    if (!isDirty || parsed.storedValue === null || parsed.error) return null
    return describeConsequence({
      definition,
      nextStored: parsed.storedValue,
      current: value,
      roi,
      entryCurrency: fieldCurrency,
      reportingCurrency,
    })
  }, [
    isDirty,
    parsed.storedValue,
    parsed.error,
    definition,
    value,
    roi,
    fieldCurrency,
    reportingCurrency,
  ])

  const canSubmit = canEdit && isDirty && !parsed.error && !parsed.empty && !isSaving

  const handleSave = async () => {
    if (!canSubmit || parsed.storedValue === null) return
    // The currency the FIELD was asking in, not the company's — see `onSave`'s doc.
    await onSave(parsed.storedValue, fieldCurrency)
  }

  const provisional = Boolean(value?.provisional)

  // ONE function decides the marker here and the counts in the page header above, so
  // "3 of 4 supplied · 1 unconfirmed" and the markers under it cannot disagree. See
  // `metricInputMarkerState`.
  const marker = <MetricInputMarker state={metricInputMarkerState(value)} />

  return (
    /*
      ── ⚠️ A FLEX COLUMN, SO THE ACTION ROW CAN SIT ON THE CARD'S FLOOR ────────────────

      MEASURED at 1280x800, sidebar EXPANDED (788.5px column), after the "Used by" panel and
      the formula came out. All four cards are 328px — `height: 100%` in a Grid row — but the
      CONTENT no longer fills them equally, because the descriptions wrap differently:

          Needed / Needed / Supplied   action row at 263 from card top, 25px below it
          Needs review                 action row at 242 from card top, 46px below it

      So the Needs review card's buttons sat 21px ABOVE its row-mate's, with almost twice the
      dead space underneath. Two cards side by side, same height, buttons on different lines —
      which reads as one card being broken rather than as one card being shorter.

      `mt: 'auto'` on the action row fixes it by pushing that row to the bottom of the column,
      which turns the leftover into consistent padding above a consistent action row. That is
      the whole argument for pinning: the dead space does not go away, it just stops being
      distributed arbitrarily.

      ⚠️ NOT masonry, and NOT an internal scroll. Masonry breaks reading order (a grid is read
      across, not down a column), and variable-height cards reintroduce the neighbour-resize
      shift that `STANDING_MIN_HEIGHT_PX` was written to eliminate. An internal scrollbar hides
      content inside a card that is already short.

      Card mode only. A popover field has no row, no neighbour and no `height: 100%`, so a
      flex column there would stretch nothing and `mt: 'auto'` would have no free space to
      absorb.
    */
    <Box
      sx={
        compact ? undefined : { height: '100%', display: 'flex', flexDirection: 'column' }
      }
    >
      {/*
        TITLE -> DESCRIPTION -> RELATIONSHIP -> INPUT.

        The title is the house 20px card title, not a 14px field label, and the
        description is directly under it (§40) rather than under the control. The
        reader meets what the figure IS before they meet the box they type it into,
        which is the order the question arrives in.
      */}
      {compact ? null : (
        <CardSectionHeader
          title={definition.label}
          titleAdornment={marker}
          description={
            <Box component="span" sx={{ display: 'block', maxWidth: PROSE_MEASURE_PX }}>
              {definition.description}
            </Box>
          }
        />
      )}

      <Box sx={{ mt: compact ? 0 : 4 }}>
      {/*
        `Used by ...` sits in the same block as the input rather than in a panel floating
        above it.

        ⚠️ IT IS NOT THE CONTROL'S LABEL, and this block used to say it was. That framing is
        what put it in a `FieldLabel` and gave it a field label's 8px gap — see the note at
        the render site. It is a provenance row: the control's accessible name is its
        `aria-label` and its visible name is the card title.

        The wrapper is a `Box`, NOT a `Stack`, and that still matters for the INPUT's own
        label in compact mode — §31's "a field is one element" trap is precisely that a Stack
        parent injects ITS spacing between label and control and silently overrides
        `FieldLabel`'s `mb`. Do not convert this to a Stack to get the gap; the gap is set
        explicitly on the provenance row instead.

        Compact mode keeps the figure's name as its label (the popover has no card title to
        carry it) and passes `showUsedBy={false}`, because there the metric IS the surrounding
        card and a "used by" line would tell that reader nothing.

        Compact mode keeps the figure's name as its label (the popover has no card title to
        carry it) and passes `showUsedBy={false}`, because there the metric IS the surrounding
        card and a "used by" line would tell that reader nothing.
      */}
      {/*
        ⚠️ NOT WRAPPED IN `FieldLabel`, AND THAT IS NOT A §31 VIOLATION — it is what §31
        actually asks for. §31 governs the gap between a field's LABEL and its control, and
        fixes it at 8px in one place so no call site invents its own. This line is not that
        label: the control's accessible name is its `aria-label` and its visible name is the
        card title (see the `NO label IN CARD MODE` note below). "Used by <metric>" is a
        provenance row stating what the figure FEEDS.

        It was wrapped in `FieldLabel` anyway, which contributed exactly one thing: an 8px
        `mb`. `UsedByLabel` brings its own `Stack`, its own gauge glyph and its own
        `body3`/`text.secondary` `Typography`, so nothing else was inherited — the wrapper was
        borrowing a field-label margin for a row that is not a field label, which is how the
        card ended up with a metadata line crowding the input at the canonical 8px.

        8px is right under a plain word like "Email". It reads tight under a row carrying an
        icon and an underlined link, because that row has more visual weight and says
        something different in kind. 16px lets it read as its own line rather than as a label
        stuck to the box.

        So the gap belongs to the CARD's layout, which is free to choose it, and `FieldLabel`
        keeps owning the one number §31 exists to protect. If `Input.label` is ever widened to
        `ReactNode` (see the note above), this does NOT become a `label` — it stays a sibling.
      */}
      {!compact && showUsedBy && (
        <Box sx={{ mb: 4 }}>
          <UsedByLabel definition={definition} />
        </Box>
      )}
      <Input
        label={compact ? definition.label : undefined}
        // Compact mode has no card title to hang the marker off, so it rides in
        // `FieldLabel`'s own adornment slot — the same mark in the same relation to the
        // same words, rather than a second treatment for the popover.
        labelAdornment={compact ? marker : undefined}
        /*
          ⚠️ NO `label` IN CARD MODE — and note the label rendered above is a different
          statement, not this one. The card title IS this control's name, and passing it here
          as well would print the same words twice, eight pixels apart; "Used by <metric>"
          names what the field FEEDS, which the title does not say. The accessible name is
          attached through `aria-label` instead, so the control is still named for a screen
          reader with nothing rendered twice for everyone else.
        */
        // The unit is IN the field — the first of this feature's three defences
        // against a units error (see `metricInputFormat`).
        startAdornment={adornment.start}
        endAdornment={adornment.end}
        /*
          GROUPED AT REST, RAW UNDER THE CARET. `draft` is never rewritten — this is a
          display swap only, which is what keeps `isDirty` honest (a grouped draft would
          mark an untouched field dirty and light up Save) and keeps re-grouping away
          from the caret between keystrokes. See `groupEntryForDisplay`.

          The caret does land at the end on focus, because the value it is editing
          changes at that moment. Accepted: these are four fields set once, and the
          alternative — grouping while typing — is the caret bug this avoids.
        */
        value={showRawEntry ? draft : groupEntryForDisplay(definition, draft)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(next: string) => setDraft(next)}
        /*
          ⚠️ `readOnly`, NOT `disabled`, and the difference is the whole point of the
          §27 split. `disabled` fades the control to 0.4 opacity and skips it in
          keyboard nav — measured here, it made the stored figure genuinely hard to
          read. But being able to READ the figure is exactly why this page is gated on
          `view_analytics` rather than on the edit permission: hiding (or greying past
          legibility) the number a metric rests on makes that metric unauditable, on a
          provenance product. `@repo/ui`'s `Input` has a read-only mode for precisely
          this case — full contrast, non-editable, no focus border.

          "You cannot change this" is carried by the disabled Save, its tooltip and the
          explicit line at the bottom of this field, all of which say WHICH permission
          — which a greyed box never could.
        */
        readOnly={!canEdit}
        error={Boolean(parsed.error)}
        placeholder={definition.unit === 'rate' ? '0' : '0.00'}
        // `inputMode` rather than `type="number"`: a number input silently discards a
        // pasted "$250,000" and hides the commas people paste out of a spreadsheet,
        // which `parseMetricInput` handles deliberately.
        inputProps={{ inputMode: 'decimal', 'aria-label': definition.label }}
      />
      </Box>

      {compact && (
        <Typography
          variant="body3"
          color="text.secondary"
          sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}
        >
          {definition.description}
        </Typography>
      )}

      {/*
        ── THE STALE-CURRENCY LINE (VRG-497) ────────────────────────────────────────

        The brand switched reporting currency and this figure is still denominated in
        the old one. The epic's settled answer is KEEP AND FLAG:

          NOT CONVERTED  — a read-time FX conversion makes every historical figure drift
                           as rates move, so last month's number stops matching the
                           screenshot a brand took of it (migration 0096's header).
          NOT DISCARDED  — forcing re-entry punishes a brand for switching and risks
                           losing a figure nobody has to hand. The number is theirs.
          NOT RELABELLED — the field above still draws it with ITS OWN symbol, so the
                           amount is never silently restated as another currency's.

        So it stays, reads correctly, and says what it is doing — which is nothing.

        ⚠️ VISIBLE AND AMBER, not a tooltip and not secondary text. Every other caveat on
        this surface is a FACT about a correct number and is deliberately quiet; this one
        is a figure that has stopped working, which is the case loudness is FOR. A reader
        who does not open a tooltip would otherwise see a figure, its provenance line and
        a confirmed marker, and conclude it is driving the dashboard.

        ⚠️ THE CLAIM IS "cannot feed a metric reported in <X>", not "is not counting
        toward your return". The stronger sentence is true today and true for the wrong
        reason on two of the four fields: acquisition cost and lifetime value are ALSO
        not counting, because the metric they feed has no resolver at all, and blaming
        the currency there would send someone to fix a thing that is not the problem.
      */}
      {stale && value && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', pt: '3px' }}>
            <CircleAlertIcon
              size={16}
              strokeWidth={1}
              color={theme.palette.warning.main}
              aria-hidden
            />
          </Box>
          <Typography
            variant="body3"
            sx={{
              display: 'block',
              lineHeight: 1.5,
              color: theme.palette.warning.main,
            }}
          >
            {/* §15 — sentences, not em-dash-joined clauses. */}
            {`Recorded in ${fieldCurrency}, but you now report in ${reportingCurrency.toUpperCase()}. Vera never converts between currencies, so this figure cannot feed a metric reported in ${reportingCurrency.toUpperCase()}. Clear it and enter the amount in ${reportingCurrency.toUpperCase()} to use it again.`}
          </Typography>
        </Stack>
      )}

      {/*
        ── Defence 2 and 3: the parsed value, and its magnitude in words ─────────────

        ⚠️ `isDirty` — IN FLIGHT ONLY, and that gate is the point of the echo.

        It used to render at rest, which put `$500.00` under an input already reading
        `500` beside a `$`, above "Set in Vera on Aug 28, 2026". Three statements of one
        fact, and the echo lost the argument for the slot because the provenance line is
        the one saying something the input does not.

        The echo is a verification aid for a value BEING TYPED: `250000` where `2500` was
        meant is a valid number no validator can reject, so the only defence is a human
        reading an absurd magnitude — and "two hundred fifty thousand dollars" is what
        makes it absurd. None of that applies to a figure someone entered last month and
        has come back to look at.

        What the echo was ALSO doing incidentally at rest — making a long digit string
        legible — is now done in the input itself (`groupEntryForDisplay`), where the
        number already is, rather than by a second line restating it.
      */}
      {isDirty && !parsed.empty && !parsed.error && parsed.display && (
        <Typography
          variant="body3"
          sx={{ display: 'block', mt: 2, color: theme.palette.text.primary }}
        >
          {parsed.display}
          {parsed.magnitude && (
            <Typography component="span" variant="body3" color="text.secondary">
              {' '}
              · {parsed.magnitude}
            </Typography>
          )}
        </Typography>
      )}

      {parsed.error && (
        <Typography variant="body3" color="error.main" sx={{ display: 'block', mt: 2 }}>
          {parsed.error}
        </Typography>
      )}

      {/*
        ── The consequence echo ────────────────────────────────────────────────────

        The one part of this field that a validator cannot replace. It states real
        resulting figures ONLY where they are derivable by exact rescale, and says
        so plainly where they are not — see `metricInputConsequence`'s header for
        why a projected figure is never invented for the unresolvable case.
      */}
      {consequence && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary[100]}`,
            backgroundColor: theme.palette.primary[50],
          }}
        >
          <Typography variant="body3" sx={{ display: 'block', lineHeight: 1.5 }}>
            {describeSettingLine(
              definition,
              parsed.storedValue as number,
              fieldCurrency,
            )}
          </Typography>
          {consequence.lines.map(line => (
            <Typography
              key={line}
              variant="body3"
              sx={{ display: 'block', lineHeight: 1.5 }}
            >
              {line}
            </Typography>
          ))}
          {consequence.multipleLine && (
            <Typography variant="body3" sx={{ display: 'block', lineHeight: 1.5, fontWeight: 600 }}>
              {consequence.multipleLine}
            </Typography>
          )}
          {consequence.unresolvedTitles.length > 0 && (
            <Typography
              variant="body3"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.5, mt: 1 }}
            >
              {consequence.unresolvedTitles.join(', ')} will be recalculated.
              {/*
                ⚠️ THE SECOND SENTENCE IS FOR THE UNSET STATE ONLY.

                "Vera cannot show the resulting figure until it has one to compare
                against" explains why no projected number follows, and that explanation
                is only news to a reader who has never supplied this figure. Once a
                figure IS set, the sentence describes a state the reader is not in — it
                reads as a caveat on a number they can already see, which is the
                non-additive amber this epic is stripping off the cards.

                `!value` is the same test the standing line below uses, so the two
                cannot disagree about which state the field is in. Note the gate is NOT
                `confidence === 'magnitude-only'`: a metric can be unresolvable while
                the input is set (the metric itself is withheld), and that reader has
                supplied their figure — telling them Vera has nothing to compare against
                blames their input for the server's missing half.
              */}
              {!value &&
                ' Vera cannot show the resulting figure until it has one to compare against.'}
            </Typography>
          )}
        </Box>
      )}

      {/*
        ── The figure's standing — ONE line, ONE slot, always present ───────────────

        "Who entered this, and when" is the first question asked when a brand disputes
        its own dashboard, which is the failure mode this whole feature is built
        around. When there IS no figure, the equivalent statement is the consequence:
        the metrics it feeds show a dash (§67 — a setting whose effect lands elsewhere
        must say where).

        These are the same statement in three states, so they share one box with a
        two-line floor. See `StandingLine`'s header for the measured layout shift that
        having them in two collapsing slots caused.

        ⚠️ COMPACT MODE IS DIFFERENT ON BOTH COUNTS, deliberately.

        The popover only ever renders inputs a metric is MISSING, and its own header
        already says so in stronger terms ("Vera has everything else this needs. Supply
        this figure and the metric activates and starts counting toward your return.").
        Repeating the
        unset consequence four lines below that is the same sentence twice, in a weaker
        amber. So compact renders the standing line only when there IS a value, exactly
        as before.

        It also takes no floor. The floor exists because the page's cards are
        `height: 100%` inside a Grid row, so one card's state changes its neighbour's
        size; a popover field has no neighbour and no row, so reserving space there would
        be 42px of blank in a 420px panel bought for nothing.

        ⚠️ Do NOT re-split this into per-state blocks, and do NOT drop the `minHeight`.
        Either change reintroduces a card whose height depends on its state, and these
        cards are `height: 100%` in a Grid row, so the one you touch resizes the one
        beside it.
      */}
      <Box sx={{ mt: 3, minHeight: compact ? undefined : `${STANDING_MIN_HEIGHT_PX}px` }}>
        {!value ? (
          compact ? null : (
          /*
            ⚠️ NECESSITY ONLY — never "supply this and it resolves".

            This sentence used to read "cannot be calculated UNTIL you supply this",
            which is a sufficiency claim, and sufficiency is not ours to promise: a
            metric this input feeds may ALSO be waiting on a measured half Vera does not
            compute yet, in which case supplying the figure moves it from one dash to
            another. The server distinguishes those (`needs_input` vs `not_computed`)
            and this component does not see that distinction — it renders from the input
            CATALOGUE, which knows which metrics an input feeds and not whether each is
            otherwise computable.

            So it states the half that is true of every named metric regardless: without
            this figure, they show a dash.
          */
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ display: 'flex', pt: '3px' }}>
                <CircleAlertIcon
                  size={16}
                  strokeWidth={1}
                  color={theme.palette.text.secondary}
                  aria-hidden
                />
              </Box>
              <Typography
                variant="body3"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: STANDING_LINE_HEIGHT }}
              >
                {describeUnsetConsequence(definition)}
              </Typography>
            </Stack>
          )
        ) : provisional ? (
          <Tooltip
            arrow
            placement="top"
            title="This figure came from your signup form and nobody has confirmed it in Vera. It is already driving the metrics below. Confirm it, or correct it."
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ cursor: 'help', display: 'inline-flex' }}
            >
              <CircleAlertIcon
                size={16}
                strokeWidth={1}
                color={theme.palette.text.secondary}
                aria-hidden
              />
              <Typography variant="body3" color="text.secondary">
                {describeProvenance(value)}
              </Typography>
            </Stack>
          </Tooltip>
        ) : (
          <Typography variant="body3" color="text.secondary">
            {describeProvenance(value)}
          </Typography>
        )}
      </Box>

      {/*
        ── The action row ──────────────────────────────────────────────────────────

        "Confirm this figure" LIVES HERE, not up beside the provenance sentence where it
        used to. Two reasons, and the second is the load-bearing one:

        1. It is an action on this figure, and the other three actions on this figure are
           in this row. A button that appears in a different place depending on state is
           a button the reader has to find twice.
        2. It was a 40px control inside a slot that collapsed to nothing in the other two
           states, and it is what made "Confirm" the single largest layout jump on the
           page — 40px measured, larger than Clear's. Moving it into a row that is always
           rendered (Save is unconditional) costs nothing and removes that jump entirely.

        ── ⚠️ ONE PRIMARY SLOT, RIGHTMOST. Confirm REPLACES Save; they never coexist ──

        The previous revision rendered Save and Confirm as two separate buttons and defended
        it with an invariant:

            `canSubmit` requires `isDirty`.  Confirm renders only when `!isDirty`.

        The invariant is true, and it is the argument for collapsing them rather than for
        keeping both. If Save can NEVER be enabled while Confirm is on screen, then the Save
        rendered beside Confirm is a permanently-inert control — a button that exists only to
        be greyed out, in the one state where the reader has a real action to take. So the two
        share ONE slot and the slot renders whichever is live:

            provisional AND clean  ->  Confirm this figure
            anything else          ->  Save

        Editing a provisional figure swaps the button back to Save, which is correct rather
        than incidental: once you have changed the number, saving it IS the confirmation — the
        same mutation with `provisional: false` either way.

        The three states in full, left to right:

            unset / clean         Save (DISABLED, §54 — nothing typed yet)
            dirty                 Discard changes · Save (LIVE)
            provisional / clean   Clear · Confirm this figure (LIVE)
            confirmed / clean     Clear · Save (DISABLED)

        §33 reads cleanly in all four: exactly one primary, always RIGHTMOST, with the
        destructive secondary (`Clear`) immediately before it. Note this also fixes a
        pre-existing violation — Save used to be primary AND leftmost, which is the inverse of
        the rule.

        ⚠️ The primary's IDENTITY changes with state but its POSITION never does. That is the
        invariant to preserve: the reader learns that the rightmost button commits the card,
        and the label tells them what committing means right now. Do not "restore" Save
        alongside Confirm for consistency — a permanently disabled button is not consistency,
        it is a dead control.
      */}
      {/*
        `mt: 'auto'` pins this to the card's floor (see the root Box). `pt: 3` keeps the 12px
        it always had between the standing line and the buttons — `auto` alone collapses to
        ZERO on a card whose content already fills the height, which is the tall-card case.
      */}
      <Stack
        direction="row"
        spacing={3}
        sx={{ mt: compact ? 3 : 'auto', pt: compact ? 0 : 3, flexWrap: 'wrap' }}
        useFlexGap
      >
        {isDirty && (
          // Cancel stays enabled and is a DISCARD here, not an exit (§54) — the field
          // sits inside a page with its own navigation, so there is nothing to leave.
          <Button variant="secondary" onClick={() => setDraft(savedEntry)} disabled={isSaving}>
            Discard changes
          </Button>
        )}

        {onRemove && value && !isDirty && (
          /*
            "Clear", not "Remove" — the word the platform uses for severing a value from
            a set rather than destroying a record, and the same distinction §38 draws for
            the X-vs-Trash glyph. Nothing is deleted here that the reader thinks of as a
            record: the FIGURE goes away and the field goes back to asking for it.

            No glyph on this one, so §38's icon test does not arise — but if one is ever
            added it is an X, not a trash can. A trash can would overstate a change the
            reader can undo by typing the number back in.
          */
          <Tooltip
            arrow
            title={
              canEdit
                ? 'Clears the figure. The metrics it feeds go back to asking for it, rather than keeping a number you no longer stand behind.'
                : NO_EDIT_PERMISSION_TOOLTIP
            }
          >
            <span>
              <Button
                variant="secondary"
                destructive="hover"
                disabled={!canEdit || isSaving}
                onClick={() => onRemove()}
              >
                Clear
              </Button>
            </span>
          </Tooltip>
        )}

        {/*
          ── THE ONE PRIMARY SLOT ────────────────────────────────────────────────────────

          Rightmost, always rendered, and it is EITHER Confirm or Save — never both. See the
          block above the Stack for why collapsing them is the right reading of the invariant.

          CONFIRM is a save of the SAME number with `provisional: false`, which is why it is
          not gated on `isDirty` — the change is to the figure's STANDING, not to its value.
          It gives way to Save the moment the field is dirty, because "confirm the stored
          number" is an ambiguous instruction while an edit is pending: saving the edit is
          what confirms it.

          Wrapped in a `span` because a disabled button emits no hover events, so the tooltip
          needs a live element to hang off — the same shape `PermissionGate` uses. Not
          `PermissionGate` itself: the disabled state is ALSO driven by dirtiness and
          validity, and that component's `disable` mode overwrites `disabled` wholesale
          rather than OR-ing with it.
        */}
        <Tooltip title={canEdit ? '' : NO_EDIT_PERMISSION_TOOLTIP} arrow>
          <span>
            {provisional && !isDirty ? (
              <Button
                variant="primary"
                disabled={!canEdit || isSaving}
                // `fieldCurrency`, matching the other call site — the unit the field was
                // ASKING in, not the company's. See `onSave`'s own doc.
                onClick={() => stored !== null && onSave(stored, fieldCurrency)}
              >
                {isSaving ? 'Saving…' : 'Confirm this figure'}
              </Button>
            ) : (
              <Button variant="primary" disabled={!canSubmit} onClick={handleSave}>
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </span>
        </Tooltip>
      </Stack>

      {!canEdit && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
          <InfoIcon size={16} strokeWidth={1} color={theme.palette.text.secondary} />
          <Typography variant="body3" color="text.secondary">
            You can see this figure but not change it. Editing needs the “Edit analytics
            inputs” permission.
          </Typography>
        </Stack>
      )}
    </Box>
  )
}
